<template>
  <b-modal id="modalTextEditor" ref="modalTextEditor" :title="title" size="xl" @ok="updateNotes" @shown="setInitialFocus">
    <b-alert :show="message.countdown || true" fade dismissible v-for="message in messages" :key="message.text" :variant="message.variant || 'danger'">{{ message.text }}</b-alert>
    <b-alert variant="warning" :show="!canUpdate">You do not have access to update notes, so this view is read only!</b-alert>
    <b-alert variant="info" :show="canUpdate">Hint: Drag and Drop note lines to re-order. Hit enter while editing a line to add a blank one beneath it. Hit backspace or delete in an empty line to delete it.</b-alert>
    <b-button-toolbar class="pb-1" v-if="canUpdate">
      <b-button-group class="mx-1 mb-1 mb-md-0" size="md">
        <b-button variant="success" v-on:click="addLine()">Add New</b-button>
        <b-button variant="danger" v-on:click="deleteLine()" v-if="orderedLines.length">Delete All</b-button>
      </b-button-group>
    </b-button-toolbar>
    <div class="content">
      <draggable v-model="orderedLines" draggable=".app-web-text-line" @end="dragLineToPosition" class="form-row">
        <div class="col-sm-12 app-web-text-line pb-1" v-for="(line, lineIndex) in orderedLines" :key="lineIndex">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text text-right">{{ formatLineNumber(lineIndex + 1) }}</span>
            </div>
            <input ref="lines" type="text" class="form-control" maxlength="64" v-model="line.text" readonly v-if="!canUpdate">
            <input ref="lines" type="text" class="form-control" maxlength="64" v-model="line.text"
              @paste="pasteContent(line, $event)" @keyup.enter="addLine(line)" @keyup.ctrl.delete="deleteLine(line)"
              @keydown.exact.delete="checkForEmptyLine(line)" @keyup.exact.delete="deleteEmptyLine(line)"
              @keydown.exact.up="moveFocusToLine(line, false, true, $event)" @keydown.exact.down="moveFocusToLine(line, false, false, $event)"
              @keydown.exact.ctrl.up="moveFocusToLine(line, true, true, $event)" @keydown.exact.ctrl.down="moveFocusToLine(line, true, false, $event)"
              v-if="canUpdate">
          </div>
        </div>
      </draggable>
    </div>
  </b-modal>
</template>

<script>
  export default {
    name: "generic-host-note-editor",
    props: {
      type: {
        type: String,
        required: true
      },
      setCode: {
        type: String,
      },
      searchKey: {
        type: String,
      },
      title: {
        type: String,
      }
    },
    created() {
    },
    mounted() {
      this.init();
    },
    beforeDestroy() {
    },
    watch: {
    },
    data() {
      return {
        state: window.appState,
        messages: [ ],
        note: { },
        orderedLines: [ ],
        perms: {
          canEditSalesNotes: appHelpers.security.hasPermission('salesnotes', 'edit'),
          canEditPurchasingNotes: appHelpers.security.hasPermission('purchasingnotes', 'edit'),
        },
        canUpdate: false,
      };
    },
    computed: {
    },
    methods: {
      encodeUrl: appHelpers.url.encode,
      formatDate: appHelpers.formatters.formatDate,
      formatDateSansTime: appHelpers.formatters.formatDateSansTime,
      formatDateSansTimeShort: appHelpers.formatters.formatDateSansTimeShort,
      formatCurrency: appHelpers.formatters.formatCurrency,
      formatCurrencyInt: appHelpers.formatters.formatCurrencyInt,
      formatFloat: appHelpers.formatters.formatFloat,
      formatInt: appHelpers.formatters.formatInt,
      formatPercent: appHelpers.formatters.formatPercent,
      formatLineNumber(value) {
        let ret = (value || 0).toString();
        while (ret.length < 3) {
          ret = '0' + ret;
        }
        return ret;
      },
      alert(message, clear) {
        this.$emit('alert', message, clear);
      },
      init() {
      },
      editNotes() {
        appHelpers.loader.start();
        let promises = [ ];
        if ((this.type || '') === 'sales') {
          promises.push(apiClient.host.note.getSalesNotes(this.state.currentTenantId, this.setCode, this.searchKey));
          this.canUpdate = this.perms.canEditSalesNotes;
        }
        if ((this.type || '') === 'purchasing') {
          promises.push(apiClient.host.note.getPurchasingNotes(this.state.currentTenantId, this.setCode, this.searchKey));
          this.canUpdate = this.perms.canEditPurchasingNotes;
        }
        Promise.all(promises).then((response) => {
          this.note = response[0].body;
          this.orderedLines = [ ];
          if (this.note && this.note.ALL_TEXT_TXT) {
            this.note.ALL_TEXT_TXT.forEach((noteLine) => {
              this.orderedLines.push(noteLine);
            });
          }
          if (!this.orderedLines.length) {
            this.addLine();
          }
          this.$refs.modalTextEditor.show();
        }, (response) => {
          this.messages = appHelpers.message.getFromResponse(response);
        }).finally(() => {
          appHelpers.loader.stop();
        });
      },
      setInitialFocus() {
        if (this.orderedLines && this.orderedLines.length) {
          Vue.nextTick().then(() => {
            this.$refs.lines[0].focus();
          });
        }
      },
      updateNotes() {
        if (this.canUpdate) {
          appHelpers.loader.start();
          let promises = [ ];
          if ((this.type || '') === 'sales') {
            promises.push(apiClient.host.note.upsertSalesNotes(this.state.currentTenantId, this.setCode, this.searchKey, this.orderedLines));
          }
          if ((this.type || '') === 'purchasing') {
            promises.push(apiClient.host.note.upsertPurchasingNotes(this.state.currentTenantId, this.setCode, this.searchKey, this.orderedLines));
          }
          Promise.all(promises).then((response) => {
            appHelpers.audit.logEntityAction('Updated', `Notes for set ${this.setCode} and key`, this.searchKey);
            this.alert(appHelpers.message.create(`Notes have been been Updated!`, 'success'));
            this.$emit('updated');
          }, (response) => {
            this.messages = appHelpers.message.getFromResponse(response);
            this.$refs.modalTextEditor.show();
          }).finally(() => {
            appHelpers.loader.stop();
          });
        }
      },
      moveLineByOffset(line, offset) {
        let lineIndex = this.orderedLines.indexOf(line);
        this.orderedLines.splice(lineIndex, 1);
        this.orderedLines.splice(lineIndex + offset, 0, line);
      },
      moveLineToPosition(line, position) {
        let lineIndex = this.orderedLines.indexOf(line);
        this.orderedLines.splice(lineIndex, 1);
        this.orderedLines.splice(position, 0, line);
      },
      dragLineToPosition(event) {
      },
      addLine(line, textContent) {
        let lineIndex = line ? (this.orderedLines.indexOf(line) + 1) : this.orderedLines.length;
        let newLine = { id: 0, text: (textContent || '') };
        this.orderedLines.splice(lineIndex, 0, newLine);
        
        if (!textContent) {
          Vue.nextTick().then(() => {
            this.$refs.lines[lineIndex].focus();
          });
        }

        return newLine;
      },
      checkForEmptyLine(line) {
        if (line) {
          let oldIsEmpty = line.isEmpty || false;
          if (line.text) {
            line.text = appHelpers.text.replace(line.text, '\r', '');
          }
          line.isEmpty = !line.text;
          //If key is being held down, allow delete to proceed without waiting for the keyup event to fire
          if (oldIsEmpty && line.isEmpty) {
            //Hack to make sure event doesn't fire twice if keyup event does actually fire now
            line.isEmpty = false;
            this.deleteLine(line);
          }
        }
      },
      deleteEmptyLine(line) {
        if (line && line.isEmpty) {
          this.deleteLine(line);
        }
      },
      deleteLine(line, skipFocus) {
        if (line) {
          let lineIndex = this.orderedLines.indexOf(line);
          this.orderedLines.splice(lineIndex, 1);

          if (this.orderedLines && this.orderedLines.length) {
            Vue.nextTick().then(() => {
              this.$refs.lines[Math.min(this.$refs.lines.length - 1, Math.max(lineIndex - 1, 0))].focus();
            });
          }
        } else {
          this.orderedLines = [ ];
        }
      },
      pasteContent(line, event) {
        let currentLine = line;
        let pasteData = (event.clipboardData || window.clipboardData).getData('text');
        let pasteDataLines = pasteData.split('\n');
        let maxLength = 64;
        let maxFirstLineLength = (64 - (currentLine.text || '').length);
        if (pasteDataLines.length > 1 || (pasteDataLines[0].length > maxFirstLineLength)) {
          pasteDataLines.forEach((pasteDataLine, pasteDataLineIndex) => {
            while (pasteDataLine.length) {
              let pasteDataChunk = pasteDataLine.length > maxLength ? pasteDataLine.substr(0, maxLength) : pasteDataLine;
              currentLine = this.addLine(currentLine, pasteDataChunk);
              pasteDataLine = pasteDataLine.substr(pasteDataChunk.length);
            }
          });
          event.stopPropagation();
          event.preventDefault();
        }
      },
      moveFocusToLine(line, ctrlFlag, upFlag, event) {
        if (line) {
          let lineIndex = this.orderedLines.indexOf(line);
          let newLineIndex = -1;
          if (upFlag && lineIndex > 0) {
            if (!ctrlFlag) {
              newLineIndex = lineIndex - 1;
            } else {
              newLineIndex = 0;
            }
          }
          if (!upFlag && lineIndex < this.orderedLines.length - 1) {
            if (!ctrlFlag) {
              newLineIndex = lineIndex + 1;
            }
            else {
              newLineIndex = this.orderedLines.length - 1;
            }
          }
          if (newLineIndex != -1) {
            Vue.nextTick().then(() => {
              this.$refs.lines[newLineIndex].focus();
            });
            event.stopPropagation();
            event.preventDefault();
          }
        }
      }
    }
  };
</script>